import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import NeoButton from "./NeoButton";
import NeoInput from "./NeoInput";
import NeoEmptyState from "./NeoEmptyState";
import NeoLoader from "./NeoLoader";

/**
 * NeoFinancialTable
 * Componente premium de tabela financeira e de alta fidelidade para o NeoApp.
 * Totalmente livre de estilos inline, utilizando classes do Neo Design System.
 */
const NeoFinancialTable = ({
    columns = [],
    data = [],
    keyField = "id",
    loading = false,
    emptyMessage = "Nenhum registro encontrado",
    emptySubMessage = "Ajuste os filtros ou adicione novos lançamentos para visualizar.",
    itemsPerPageOptions = [5, 10, 20, 50],
    defaultItemsPerPage = 5,
    summaryData = [], // [{ label, value, count, icon, color, bg }]
    summaryComponent = null,
    searchPlaceholder = "Buscar registros...",
    searchValue = "",
    onSearchChange = null,
    renderRowDetails = null, // (row) => ReactNode para expandir a linha
    headerActions = null, // Botões extras do lado direito do cabeçalho
    className = "",
    style = {},
    ...props
}) => {
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(defaultItemsPerPage);
    const [expandedRows, setExpandedRows] = useState({});

    // Reseta página quando os dados mudam
    useEffect(() => {
        setCurrentPage(1);
    }, [data.length, itemsPerPage]);

    const totalRows = data.length;
    const totalPages = Math.ceil(totalRows / itemsPerPage) || 1;
    const activePage = Math.min(Math.max(1, currentPage), totalPages);

    const paginatedData = useMemo(() => {
        const startIndex = (activePage - 1) * itemsPerPage;
        return data.slice(startIndex, startIndex + itemsPerPage);
    }, [data, activePage, itemsPerPage]);

    const canGoBack = activePage > 1;
    const canGoForward = activePage < totalPages;

    const toggleRow = (rowId) => {
        setExpandedRows(prev => ({ ...prev, [rowId]: !prev[rowId] }));
    };

    const cellStyle = (col) => {
        const width = col.width || col.style?.width;
        const minWidth = col.minWidth || col.style?.minWidth || 0;
        const maxWidth = col.maxWidth || col.style?.maxWidth;
        return {
            width: width || undefined,
            flex: width ? `0 0 ${width}` : col.flex ? `${col.flex} ${col.flex} 0` : "1 1 0",
            minWidth,
            maxWidth: maxWidth || undefined,
            justifyContent: col.align === "center" ? "center" : col.align === "right" ? "flex-end" : "flex-start",
        };
    };

    return (
        <div className={`neo-financial-table-wrapper ${className}`} style={style} {...props}>
            <NeoLoader type="overlay" loading={loading} label="Processando informações..." />

            {/* ── 1. HEADER UNIFICADO: Summary (esq) + Controles (dir) ── */}
            {(summaryData?.length > 0 || summaryComponent || onSearchChange) && (
                <div className="neo-financial-header-bar">
                    {/* Lado esquerdo: resumo financeiro */}
                    {summaryData?.length > 0 && (
                        <div className="neo-financial-summary-container">
                            {summaryData.map((item, index) => (
                                <div key={index} className={`neo-financial-summary-item ${item.statusClass || ""}`}>
                                    {item.icon && (
                                        <div
                                            className="neo-financial-summary-icon-bg"
                                            style={item.bg && !item.statusClass ? { backgroundColor: item.bg } : {}}
                                        >
                                            <i
                                                className={`mdi ${item.icon} font-size-18`}
                                                style={item.color && !item.statusClass ? { color: item.color } : {}}
                                            />
                                        </div>
                                    )}
                                    <div>
                                        <span className="neo-financial-summary-label">{item.label}</span>
                                        <span
                                            className="neo-financial-summary-value"
                                            style={item.color && !item.statusClass ? { color: item.color } : {}}
                                        >
                                            {item.value}
                                            {item.count !== undefined && (
                                                <small className="text-muted font-size-10 ms-1 fw-normal">({item.count})</small>
                                            )}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Lado direito: controles */}
                    <div className="neo-financial-controls">
                        {/* summaryComponent extra (ex: filtro de status) */}
                        {summaryComponent && (
                            <div className="neo-financial-summary-component">
                                {summaryComponent}
                            </div>
                        )}

                        {/* Itens por página */}
                        <div className="neo-financial-per-page-select-wrapper">
                            <span className="text-muted font-size-12 fw-semibold">Mostrar</span>
                            <div className="neo-financial-per-page-select">
                                <NeoInput
                                    type="select"
                                    value={itemsPerPage}
                                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                                    options={itemsPerPageOptions.map((opt) => ({ value: opt, label: String(opt) }))}
                                    size="sm"
                                    flat
                                    className="mb-0"
                                />
                            </div>
                            <span className="text-muted font-size-12 fw-semibold">por página</span>
                        </div>

                        {/* Busca */}
                        {onSearchChange && (
                            <div className="neo-financial-search-input-wrapper">
                                <NeoInput
                                    type="text"
                                    placeholder={searchPlaceholder}
                                    value={searchValue}
                                    onChange={(e) => onSearchChange(e.target.value)}
                                    size="sm"
                                    icon="mdi-magnify"
                                    className="mb-0"
                                />
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* ── 3. CORPO: TABELA FLEX EXPANSÍVEL ── */}
            <div className="neo-financial-table-container">
                {columns.length > 0 && paginatedData.length > 0 && (
                    <div className="neo-financial-table-header-row">
                        {columns.map((col, idx) => (
                            <div key={idx} style={cellStyle(col)} className="neo-financial-table-cell neo-financial-table-header-cell">
                                {col.label}
                            </div>
                        ))}

                        {/* Setinha de expansão no final */}
                        {renderRowDetails && (
                            <div className="neo-financial-table-expand-column neo-table-header-cell" />
                        )}
                    </div>
                )}

                <div className="d-flex flex-column">
                    {paginatedData.length > 0 ? (
                        paginatedData.map((row, rowIndex) => {
                            const rowKey = row[keyField] || rowIndex;
                            const isExpanded = !!expandedRows[rowKey];
                            return (
                                <React.Fragment key={rowKey}>
                                    {/* Linha Principal */}
                                    <div 
                                        className={`neo-financial-table-row ${isExpanded ? "neo-financial-table-row-expanded" : ""}`}
                                        onClick={() => renderRowDetails && toggleRow(rowKey)}
                                    >
                                        {/* Colunas Reais */}
                                        {columns.map((col, idx) => (
                                            <div key={idx} style={cellStyle(col)} className="neo-financial-table-cell font-size-13">
                                                {col.render
                                                    ? col.render(row, rowIndex, { page: activePage, rowsPerPage: itemsPerPage })
                                                    : (row[col.key] !== undefined && row[col.key] !== null ? String(row[col.key]) : "—")
                                                }
                                            </div>
                                        ))}

                                        {/* Setinha de expansão no final */}
                                        {renderRowDetails && (
                                            <div className="neo-financial-table-expand-column">
                                                <NeoButton
                                                    variant="link"
                                                    size="sm"
                                                    circle
                                                    icon={isExpanded ? "mdi-chevron-down" : "mdi-chevron-right"}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        toggleRow(rowKey);
                                                    }}
                                                    title={isExpanded ? "Recolher detalhes" : "Expandir detalhes"}
                                                />
                                            </div>
                                        )}
                                    </div>

                                    {/* Linha Expansível (Sub-menu Drawer) */}
                                    {renderRowDetails && isExpanded && (
                                        <div className="neo-financial-table-details-drawer">
                                            {renderRowDetails(row)}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })
                    ) : (
                        <div className="p-5 text-center">
                            <NeoEmptyState
                                icon="mdi-table-off"
                                title={emptyMessage}
                                description={emptySubMessage}
                            />
                        </div>
                    )}
                </div>

                {/* ── 4. BASE: PAGINAÇÃO UNIFICADA ── */}
                {data.length > 0 && (
                    <div className="neo-financial-table-footer">
                        <div className="neo-financial-table-pagination-label">
                            Exibindo {(activePage - 1) * itemsPerPage + 1}–{Math.min(activePage * itemsPerPage, totalRows)} de {totalRows}
                        </div>
                        <div className="d-flex gap-1 align-items-center">
                            <NeoButton
                                variant="outline-chocolate"
                                size="sm"
                                square
                                disabled={!canGoBack}
                                onClick={() => setCurrentPage(activePage - 1)}
                                icon="mdi-chevron-left"
                                title="Página Anterior"
                            />
                            <span className="neo-financial-table-pagination-indicator">
                                {activePage} / {totalPages}
                            </span>
                            <NeoButton
                                variant="outline-chocolate"
                                size="sm"
                                square
                                disabled={!canGoForward}
                                onClick={() => setCurrentPage(activePage + 1)}
                                icon="mdi-chevron-right"
                                title="Próxima Página"
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

NeoFinancialTable.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.shape({
        key: PropTypes.string,
        label: PropTypes.string.isRequired,
        render: PropTypes.func,
        width: PropTypes.string,
        align: PropTypes.string
    })).isRequired,
    data: PropTypes.array,
    keyField: PropTypes.string,
    loading: PropTypes.bool,
    emptyMessage: PropTypes.string,
    emptySubMessage: PropTypes.string,
    itemsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
    defaultItemsPerPage: PropTypes.number,
    summaryData: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired,
        count: PropTypes.number,
        icon: PropTypes.string,
        color: PropTypes.string,
        bg: PropTypes.string
    })),
    summaryComponent: PropTypes.node,
    searchPlaceholder: PropTypes.string,
    searchValue: PropTypes.string,
    onSearchChange: PropTypes.func,
    renderRowDetails: PropTypes.func,
    headerActions: PropTypes.node,
    className: PropTypes.string,
    style: PropTypes.object
};

export default NeoFinancialTable;
